import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { aiPodService, type AiPod } from '../../../services/aiPodService';
import { agentService } from '../../../services/agentService';
import { AiPodBuilder } from '../../../components/ai-pod/AiPodBuilder';
import { buildAiPodGraph, parseAiPodGraph } from '../../../lib/ai-pod/podGraphConversion';
import type { AiPodCanvasNode, AiPodConnection } from '../../../types/ai-pod-canvas';
import type { AiPodAgentTypeOption } from '../../../components/ai-pod/AiPodInspector';
import { Button } from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';
import ChatbotTemplateEditor from './components/ChatbotTemplateEditor';

const AiPodConfigure: React.FC = () => {
  const { aiPodId } = useParams<{ aiPodId: string }>();
  const navigate = useNavigate();
  const [chatOpen, setChatOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [aiPod, setAiPod] = useState<AiPod | null>(null);
  const [agentTypes, setAgentTypes] = useState<AiPodAgentTypeOption[]>([]);
  const [initialNodes, setInitialNodes] = useState<AiPodCanvasNode[]>([]);
  const [initialConnections, setInitialConnections] = useState<AiPodConnection[]>([]);

  useEffect(() => {
    if (!aiPodId) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const [pod, graph, agentTypesRes] = await Promise.all([
          aiPodService.getAiPodById(aiPodId),
          aiPodService.getGraph(aiPodId),
          agentService.listAgentTypes({ limit: 100, isActive: true }),
        ]);

        if (cancelled) return;

        // A chat-only pod has no workflow canvas: it configures the chatbot instead.
        if (pod.channel === 'chat') {
          navigate(`/super-ai-pod/${aiPodId}/chat`, { replace: true });
          return;
        }
        setAiPod(pod);
        const { nodes, connections } = parseAiPodGraph(graph);
        setInitialNodes(nodes);
        setInitialConnections(connections);

        const agentTypesRaw: any =
          (agentTypesRes as any)?.agentTypes || (agentTypesRes as any)?.data?.agentTypes || [];
        setAgentTypes(
          Array.isArray(agentTypesRaw)
            ? agentTypesRaw.map((a: any) => ({
                id: a.id,
                code: a.code,
                name: a.name,
                shortDescription: a.shortDescription,
              }))
            : []
        );
      } catch (e) {
        console.error('Failed to load AI Pod configuration:', e);
        toast.error('Failed to load AI Pod configuration');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [aiPodId, navigate]);

  const handleSave = async (payload: { nodes: AiPodCanvasNode[]; connections: AiPodConnection[] }) => {
    if (!aiPodId) return;
    const graph = buildAiPodGraph(payload.nodes, payload.connections);
    await aiPodService.saveGraph(aiPodId, graph);
  };

  if (loading) {
    return <div className="flex h-screen items-center justify-center text-gray-500">Loading…</div>;
  }

  if (!aiPod) {
    return <div className="flex h-screen items-center justify-center text-gray-500">AI Pod not found.</div>;
  }

  return (
    // Fills the space under the app's top navigation; the step inspector is positioned
    // inside this area so it never covers the navigation bar.
    <div className="flex h-[calc(100dvh-5.5rem)] min-h-[32rem] flex-col">
      <div className="flex items-center gap-3 border-b border-gray-100 bg-white px-4 py-3">
        <Button tone="outline-subtle" className="rounded-md border px-3 py-1.5 text-sm" onClick={() => navigate('/super-ai-pod')}>
          ← Back
        </Button>
        <div className="flex-1">
          <h1 className="text-sm font-semibold text-gray-900">{aiPod.name}</h1>
          <p className="text-xs text-gray-500">
            {aiPod.code} · {aiPod.channel === 'both' ? 'Workflow + chat' : 'Workflow'}
          </p>
        </div>
        {aiPod.channel === 'both' && (
          <Button tone="outline-subtle" className="rounded-md border px-3 py-1.5 text-sm" onClick={() => setChatOpen(true)}>
            Configure chat
          </Button>
        )}
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <AiPodBuilder
          agentTypes={agentTypes}
          initialNodes={initialNodes}
          initialConnections={initialConnections}
          onSave={handleSave}
        />
      </div>

      {aiPod.channel === 'both' && (
        <Modal isOpen={chatOpen} onClose={() => setChatOpen(false)} title={`${aiPod.name} — chat`} size="xl">
          <ChatbotTemplateEditor pod={aiPod} onSaved={setAiPod} />
        </Modal>
      )}
    </div>
  );
};

export default AiPodConfigure;
